import React, { useEffect, useState } from "react";
import { toast } from "@/state/hooks";
import {
  ShieldCheck,
  Shield,
  Eye,
  Trash2,
  Copy,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ListContainer, ListItem } from "@/ui/components/shared/ListItem";
import { useAppDialog } from "@/state/legacy_module_api";
import { useActions, useAppState } from "@/state/legacy_module_api";

const BackupPanel = () => {
  const { openDialog } = useAppDialog();
  const { recovery } = useAppState();
  const {
    generateMnemonic,
    selectVerificationWords,
    verifyMnemonic,
    completeSetup,
    loadExistingMnemonic,
    deleteMnemonic,
    clearRecoveryState,
    showRecoveryPhrase,
    hideRecoveryPhrase,
    deleteRecoveryBackup,
  } = useActions().recovery;

  useEffect(() => {
    loadExistingMnemonic();
  }, []);

  const handleGeneratePhrase = async () => {
    await generateMnemonic();
  };

  const handleStartVerification = () => {
    selectVerificationWords();
  };

  const handleVerifyPhrase = async (confirmInput: string) => {
    const isValid = verifyMnemonic(confirmInput);
    if (isValid) {
      await completeSetup();
      toast.success("Recovery phrase saved securely!");
    }
  };

  const handleCopyToClipboard = () => {
    if (recovery.mnemonic) {
      navigator.clipboard.writeText(recovery.mnemonic).then(() => {
        toast.success("Recovery phrase copied to clipboard");
      });
    }
  };

  const handleDeleteBackup = async () => {
    openDialog({
      type: "confirm",
      title: "Delete Backup",
      message:
        "Are you sure you want to delete your recovery backup? This action cannot be undone.",
      onConfirm: async () => {
        try {
          if (recovery.authKeyPair?.publicKey) {
            await deleteRecoveryBackup(recovery.authKeyPair.publicKey);
          }
          deleteMnemonic();
          toast.success("Backup deleted successfully");
        } catch (error) {
          toast.error("Failed to delete backup");
        }
      },
    });
  };

  const handleViewRecoveryPhrase = () => {
    openDialog({
      type: "confirm",
      title: "View Recovery Phrase",
      message:
        "Are you sure you want to view your recovery phrase? Make sure you're in a private location and no one can see your screen.",
      onConfirm: () => {
        showRecoveryPhrase();
      },
    });
  };

  const renderExistingBackup = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <ShieldCheck className="w-12 h-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Backup Created</h3>
            <p className="text-sm text-muted-foreground">
              Your account recovery phrase has been securely saved.
            </p>
          </div>
        </CardContent>
      </Card>

      <ListContainer>
        <ListItem
          icon={<Eye className="w-5 h-5" />}
          title="View Recovery Phrase"
          onClick={handleViewRecoveryPhrase}
          showChevron
        />
        <ListItem
          icon={<Trash2 className="w-5 h-5 text-destructive" />}
          title="Delete Backup"
          onClick={handleDeleteBackup}
          showChevron
        />
      </ListContainer>
    </div>
  );

  const renderGenerateBackup = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <Shield className="w-12 h-12 mx-auto text-orange-500" />
            <h3 className="text-lg font-semibold">Secure Your Account</h3>
            <p className="text-sm text-muted-foreground">
              Create a recovery phrase to restore your account if you lose
              access to this device.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-sm">Important Notes</strong>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Write down your recovery phrase on paper</li>
                <li>• Store it in a safe place</li>
                <li>• Never share it with anyone</li>
                <li>• This is the only way to recover your account</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={handleGeneratePhrase}
        disabled={recovery.setupStep === "generating"}
        className="w-full"
      >
        {recovery.setupStep === "generating"
          ? "Generating..."
          : "Generate Recovery Phrase"}
      </Button>
    </div>
  );

  const renderRecoveryPhrase = () => {
    if (!recovery.mnemonic) return null;

    const words = recovery.mnemonic.split(" ");

    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Your Recovery Phrase</h3>
        <div className="grid grid-cols-2 gap-2">
          {words.map((word, index) => (
            <div
              key={index}
              className="bg-card border rounded-lg p-3 text-center"
            >
              <span className="text-xs text-muted-foreground">{index + 1}.</span>
              <br />
              <strong>{word}</strong>
            </div>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleCopyToClipboard} variant="outline">
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          {recovery.isSetupComplete ? (
            <Button onClick={hideRecoveryPhrase} variant="secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button onClick={handleStartVerification}>
              <CheckCircle className="w-4 h-4 mr-2" />
              I've Saved It
            </Button>
          )}
        </div>

        <Card className="border-destructive/50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
              <div>
                <strong className="text-sm">Security Warning</strong>
                <p className="text-sm text-muted-foreground mt-1">
                  Make sure you're in a private location and no one can see your
                  screen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const VerificationComponent = ({
    onVerify,
    error,
    isLoading,
  }: {
    onVerify: (input: string) => void;
    error: string | null;
    isLoading: boolean;
  }) => {
    const [confirmInput, setConfirmInput] = useState("");

    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Confirm Your Backup</h3>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-4">
              Make sure you have saved your recovery phrase securely. Type{" "}
              <strong>CONFIRM</strong> below to save your backup.
            </p>

            <Input
              placeholder="Type CONFIRM"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />

            {error && <p className="text-sm text-destructive mt-2">{error}</p>}

            <Button
              onClick={() => onVerify(confirmInput)}
              disabled={isLoading}
              className="w-full mt-4"
            >
              {isLoading ? "Saving..." : "Save Backup"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  const verificationComponent = React.useMemo(() => {
    if (recovery.setupStep === "verifying" || recovery.setupStep === "saving") {
      return (
        <VerificationComponent
          onVerify={handleVerifyPhrase}
          error={recovery.error}
          isLoading={recovery.setupStep === "saving"}
        />
      );
    }
    return null;
  }, [recovery.setupStep, recovery.error]);

  return (
    <div className="h-full overflow-auto space-y-4 p-4">
      <h2 className="text-xl font-semibold">Account Backup</h2>

      {verificationComponent}
      {recovery.setupStep === "displaying" && renderRecoveryPhrase()}
      {recovery.isSetupComplete && renderExistingBackup()}
      {recovery.setupStep === "idle" &&
        !recovery.isSetupComplete &&
        renderGenerateBackup()}

      {recovery.error && (recovery as any).recoveryStep === "error" && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive mb-4">Error: {recovery.error}</p>
            <Button onClick={() => clearRecoveryState()}>Try Again</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BackupPanel;
